import PropTypes from 'prop-types';

export const notableTenantType = PropTypes.shape({
  buildingId: PropTypes.string.isRequired,
  companyId: PropTypes.string.isRequired,
  companyName: PropTypes.string.isRequired,
  logo: PropTypes.shape({
    description: PropTypes.string,
    path: PropTypes.string,
  }).isRequired,
});

export const propertyType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  vtsId: PropTypes.number,
  street: PropTypes.string,
  locality: PropTypes.string,
  region: PropTypes.string,
  postalCode: PropTypes.string,
  countryCode: PropTypes.string,
});

export const propertiesType = PropTypes.shape({
  [PropTypes.string]: propertyType,
});

export const paramsType = PropTypes.shape({
  id: PropTypes.string,
});

export const matchType = PropTypes.shape({
  params: paramsType,
});

export const preloadedDataType = PropTypes.shape({
  properties: PropTypes.arrayOf(propertyType),
});

export const adminUserType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  jobTitle: PropTypes.string,
  marketSlug: PropTypes.string.isRequired,
  role: PropTypes.string,
  phoneNumber: PropTypes.string,
});

export const userAnalyticsType = PropTypes.shape({
  userEmail: PropTypes.string.isRequired,
  userId: PropTypes.string,
  userFirstName: PropTypes.string,
  userLastName: PropTypes.string,
  userTitle: PropTypes.string,
  userPhone: PropTypes.string,
  userMarket: PropTypes.string,
  userAccountType: PropTypes.string,
  initialSmsOptIn: PropTypes.bool,
});

export const userType = PropTypes.shape({
  email: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  jobTitle: PropTypes.string,
  phoneNumber: PropTypes.string,
  formattedPhoneNumber: PropTypes.string,
  role: PropTypes.string,
  marketName: PropTypes.string.isRequired,
  marketSlug: PropTypes.string.isRequired,
  analyticsInformation: userAnalyticsType,
});

export const notificationsOnLoadType = PropTypes.shape({
  notice: PropTypes.string,
  alert: PropTypes.string,
});

export const configType = PropTypes.shape({
  locale: PropTypes.string.isRequired,
  translationsJSON: PropTypes.string.isRequired,
});

export const areaType = PropTypes.shape({
  magnitude: PropTypes.number.isRequired,
  unit: PropTypes.oneOf(['SquareFoot', 'SquareMeter']).isRequired,
});

export const historyType = PropTypes.object;

export const listingStatuses = [
  'inactive',
  'requested',
  'onboarding',
  'ready_for_approval',
  'on_market',
  'off_market',
  'retired',
];

export const imageType = PropTypes.shape({
  path: PropTypes.string.isRequired,
  smallPath: PropTypes.string.isRequired,
  mediumPath: PropTypes.string.isRequired,
  largePath: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
});

export const videoType = PropTypes.shape({
  posterPath: PropTypes.string.isRequired,
  thumbnailPosterPath: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
});

export const listingAmenitiesType = PropTypes.shape({
  barAvailable: PropTypes.bool,
  bikeStorageAvailable: PropTypes.bool,
  cafeAvailable: PropTypes.bool,
  communalLobbySpace: PropTypes.bool,
  communalOutdoorSpace: PropTypes.bool,
  communalRoofAccess: PropTypes.bool,
  expandable: PropTypes.bool.isRequired,
  foodServiceAvailable: PropTypes.bool,
  furnished: PropTypes.oneOf(['yes', 'no', 'partial']),
  generalStoreAvailable: PropTypes.bool,
  gymAvailable: PropTypes.bool,
  keyCardBuildingEntry: PropTypes.bool,
  keyCardSpaceEntry: PropTypes.bool,
  lobbyAttendant: PropTypes.bool,
  lobbyBeverageAvailable: PropTypes.bool,
  lobbyFoodAvailable: PropTypes.bool,
  messengerCenter: PropTypes.bool,
  parkingAvailable: PropTypes.bool,
  passengerElevatorCount: PropTypes.number,
  petsAllowed: PropTypes.bool,
  pharmacyAvailable: PropTypes.bool,
  propertyManagerOnSite: PropTypes.bool,
  restaurantAvailable: PropTypes.bool,
  serviceElevatorCount: PropTypes.number,
  spaAvailable: PropTypes.bool,
  storageAvailable: PropTypes.bool,
  turnstiles: PropTypes.bool,
  twentyFourSevenAccess: PropTypes.bool,
});

export const buildingAmenitiesType = PropTypes.shape({
  barAvailable: PropTypes.bool,
  bikeStorageAvailable: PropTypes.bool,
  cafeAvailable: PropTypes.bool,
  communalLobbySpace: PropTypes.bool,
  communalOutdoorSpace: PropTypes.bool,
  communalRoofAccess: PropTypes.bool,
  foodServiceAvailable: PropTypes.bool,
  generalStoreAvailable: PropTypes.bool,
  gymAvailable: PropTypes.bool,
  keyCardBuildingEntry: PropTypes.bool,
  lobbyAttendant: PropTypes.bool,
  lobbyBeverageAvailable: PropTypes.bool,
  lobbyFoodAvailable: PropTypes.bool,
  messengerCenter: PropTypes.bool,
  parkingAvailable: PropTypes.bool,
  petsAllowed: PropTypes.bool,
  pharmacyAvailable: PropTypes.bool,
  propertyManagerOnSite: PropTypes.bool,
  restaurantAvailable: PropTypes.bool,
  spaAvailable: PropTypes.bool,
  storageAvailable: PropTypes.bool,
  turnstiles: PropTypes.bool,
  twentyFourSevenAccess: PropTypes.bool,
});

export const listingAgentsType = PropTypes.arrayOf(
  PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    role: PropTypes.string,
    phone: PropTypes.string,
    company: PropTypes.string,
  }),
);

export const listingBaseRentsType = PropTypes.arrayOf(
  PropTypes.shape({
    month: PropTypes.number,
    value: PropTypes.number,
  }),
);

export const roomAmenitiesType = PropTypes.shape({
  deskCount: PropTypes.number,
  electricAdjustableDeskCount: PropTypes.number,
  deskChairCount: PropTypes.number,
  tableCount: PropTypes.number,
  workbenchCount: PropTypes.number,
  loungeChairCount: PropTypes.number,
  stoolCount: PropTypes.number,
  couchesCount: PropTypes.number,
  conferenceTableCount: PropTypes.number,
  storageClosetArea: PropTypes.bool,
  kitchenTableCount: PropTypes.number,
  kitchenChairCount: PropTypes.number,
  refrigeratorCount: PropTypes.number,
  sinkCount: PropTypes.number,
  dishwasherCount: PropTypes.number,
  microwaveCount: PropTypes.number,
  ovenCount: PropTypes.number,
  stoveCount: PropTypes.number,
  lockers: PropTypes.bool,
  poolTableCount: PropTypes.number,
  airHockeyTableCount: PropTypes.number,
  pingPongTableCount: PropTypes.number,
  vendingMachineCount: PropTypes.number,
  bathroomType: PropTypes.oneOf(['stalls', 'unisex_with_sink', 'unisex_shared_sink']),
  bathroomSinkCount: PropTypes.number,
  bathroomStallCount: PropTypes.number,
});

export const roomType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  photos: PropTypes.arrayOf(imageType),
  capacity: PropTypes.string,
  amenities: roomAmenitiesType,
});

export const owningCompanyType = PropTypes.shape({
  companyName: PropTypes.string.isRequired,
  companyId: PropTypes.string.isRequired,
  logo: PropTypes.shape({
    path: PropTypes.string,
    description: PropTypes.string,
  }),
});

export const buildingInListingType = PropTypes.shape({
  cityState: PropTypes.string,
  description: PropTypes.string,
  formattedAddress: PropTypes.string,
  neighborhod: PropTypes.string,
  notableTenants: PropTypes.arrayOf(PropTypes.string),
  numberOfFloors: PropTypes.number,
  photo: imageType,
  photoName: PropTypes.string,
  primaryName: PropTypes.string,
  rentableArea: areaType,
  slug: PropTypes.string,
  // video: videoType,
  yearBuilt: PropTypes.number,
  yearRenovated: PropTypes.number,
  owningCompany: PropTypes.string,
});

export const searchListingType = PropTypes.shape({
  buildingName: PropTypes.string.isRequired,
  buildingSlug: PropTypes.string.isRequired,
  buildingStreet: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  isCurrentlyExclusive: PropTypes.bool.isRequired,
  photo: imageType.isRequred,
  capacity: PropTypes.number,
  neighborhoodName: PropTypes.string,
  smallestPrice: PropTypes.number,
  shortestLeaseTerm: PropTypes.string,
  onMarket: PropTypes.bool.isRequired,
  area: areaType,
});

export const spaceProviderType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  logo: imageType.isRequired,
  slug: PropTypes.string,
});

export const listingType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  status: PropTypes.oneOf(listingStatuses).isRequired,
  buildingName: PropTypes.string.isRequired,
  buildingSlug: PropTypes.string.isRequired,
  favorited: PropTypes.bool,
  dateAvailable: PropTypes.string,
  availableToday: PropTypes.bool.isRequired,
  availabilityType: PropTypes.string,
  leaseType: PropTypes.string,
  capacity: PropTypes.string,
  area: areaType,
  baseRents: listingBaseRentsType,
  amenities: listingAmenitiesType.isRequired,
  listingAgents: listingAgentsType.isRequired,
  cityState: PropTypes.string,
  photos: PropTypes.arrayOf(imageType),
  video: videoType,
  rooms: PropTypes.arrayOf(roomType).isRequired,
  daysOnMarket: PropTypes.number,
  condition: PropTypes.string,
  building: buildingInListingType.isRequired,
  isCurrentlyExclusive: PropTypes.bool.isRequired,
  marketName: PropTypes.string,
  tourEmbedUrl: PropTypes.string,
  spaceProvider: spaceProviderType,
});

export const buildingStatuses = ['inactive', 'onboarding', 'ready_for_approval', 'live', 'retired'];

export const CERTIFICATION_LEVELS = ['certified', 'silver', 'gold', 'platinum'];

export const buildingListingType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  floorName: PropTypes.string.isRequired,
  suiteName: PropTypes.string.isRequired,
  area: areaType,
  availabilityDate: PropTypes.string,
  availabilityType: PropTypes.string.isRequired,
  availableToday: PropTypes.bool.isRequired,
  furnished: PropTypes.bool.isRequired,
  baseRents: listingBaseRentsType.isRequired,
  previewPhoto: imageType.isRequired,
  daysOnMarket: PropTypes.number.isRequired,
});

export const transitOptionsType = PropTypes.shape({
  distanceInMiles: PropTypes.number.isRequired,
  lines: PropTypes.arrayOf(PropTypes.string).isRequired,
  stopName: PropTypes.string.isRequired,
});

export const buildingType = PropTypes.shape({
  id: PropTypes.string,
  primaryName: PropTypes.string,
  slug: PropTypes.string,
  formattedAddress: PropTypes.string,
  neighborhood: PropTypes.string,
  onMarketListings: PropTypes.arrayOf(buildingListingType),
  metadata: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  photo: PropTypes.string,
  owningCompany: spaceProviderType,
  yearBuilt: PropTypes.number,
  yearRenovated: PropTypes.number,
  numberOfFloors: PropTypes.number,
  rentableBuildingArea: areaType,
  secondaryName: PropTypes.string,
  cityState: PropTypes.string,
  wiredCertification: PropTypes.oneOf(CERTIFICATION_LEVELS),
  leedCertification: PropTypes.oneOf(CERTIFICATION_LEVELS),
  description: PropTypes.string,
  transitOptions: PropTypes.arrayOf(transitOptionsType),
  marketName: PropTypes.string,
});

export const adminMediaType = PropTypes.shape({
  filename: PropTypes.string.isRequired,
  processedAt: PropTypes.string,
  path: PropTypes.string,
  status: PropTypes.oneOf(['pending', 'successful', 'failed']).isRequired,
  roomType: PropTypes.string,
});

export const dataSourceType = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  dataSource: PropTypes.string,
  dataSourceId: PropTypes.string,
});

export const adminBuildingType = PropTypes.shape({
  abbreviatedRegion: PropTypes.string,
  barAvailable: dataSourceType,
  bikeStorageAvailable: dataSourceType,
  cafeAvailable: dataSourceType,
  commonAreaWifi: dataSourceType,
  communalLobbySpace: dataSourceType,
  communalOutdoorSpace: dataSourceType,
  communalRoofAccess: dataSourceType,
  description: PropTypes.string,
  entranceCount: dataSourceType,
  flexSpaceAvailable: dataSourceType,
  foodServiceAvailable: dataSourceType,
  fiberProviders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  generalStoreAvailable: dataSourceType,
  gymAvailable: dataSourceType,
  id: PropTypes.string.isRequired,
  keyCardBuildingEntry: dataSourceType,
  landmarkBuilding: dataSourceType,
  leedCertification: dataSourceType,
  liveAt: PropTypes.string,
  locality: PropTypes.string,
  lobbyAttendant: dataSourceType,
  lobbyBeverageAvailable: dataSourceType,
  lobbyFoodAvailable: dataSourceType,
  messengerCenter: dataSourceType,
  mobileConnectivityImprovements: dataSourceType,
  neighborhood: PropTypes.string,
  numberOfFloors: PropTypes.number,
  optionToAddInternetServiceProvider: dataSourceType,
  parkingAvailable: dataSourceType,
  passengerElevatorCount: dataSourceType,
  passengerElevatorPosition: dataSourceType,
  petsAllowed: dataSourceType,
  pharmacyAvailable: dataSourceType,
  photos: PropTypes.arrayOf(adminMediaType),
  primaryName: PropTypes.string,
  primaryPropertyId: PropTypes.string.isRequired,
  properties: PropTypes.arrayOf(propertyType),
  propertyManagerOnSite: dataSourceType,
  rentableBuildingArea: areaType,
  restaurantAvailable: dataSourceType,
  retiredAt: PropTypes.string,
  serviceElevatorCount: dataSourceType,
  slug: PropTypes.string,
  spaAvailable: dataSourceType,
  status: PropTypes.oneOf(buildingStatuses).isRequired,
  storageAvailable: dataSourceType,
  tourEmbedUrl: PropTypes.string,
  turnstiles: dataSourceType,
  twentyFourSevenAccess: dataSourceType,
  video: adminMediaType,
  urgentCareDoctorOnsite: dataSourceType,
  wiredCertification: dataSourceType,
  yearBuilt: PropTypes.number,
  yearRenovated: PropTypes.number,
  yogaStudioAvailable: dataSourceType,
});

export const formType = PropTypes.shape({
  getFieldValue: PropTypes.func,
  getFieldsValue: PropTypes.func,
  getFieldDecorator: PropTypes.func,
});

export const adminListingType = PropTypes.shape({
  buildOutLevel: dataSourceType,
  drinkingFountainCount: dataSourceType,
  electricalRoom: dataSourceType,
  elevatorLocation: dataSourceType,
  exteriorSignage: dataSourceType,
  floor: PropTypes.string.isRequired,
  floorPlanCloudinaryId: PropTypes.string,
  furnished: dataSourceType,
  id: PropTypes.string.isRequired,
  interiorSignage: dataSourceType,
  keyCardSpaceEntry: dataSourceType,
  leaseType: PropTypes.string,
  maximumCapacityPersons: PropTypes.number,
  mechanicalRoom: dataSourceType,
  notMarketable: PropTypes.bool,
  passengerElevatorCount: dataSourceType,
  photos: PropTypes.arrayOf(adminMediaType).isRequired,
  serviceElevatorCount: dataSourceType,
  sharedFloorBathroomStallCount: dataSourceType,
  status: PropTypes.oneOf(listingStatuses).isRequired,
  suite: PropTypes.string,
  tourEmbedUrl: PropTypes.string,
  url: PropTypes.string,
  video: adminMediaType,
  windowSidesCount: dataSourceType,
  building: PropTypes.shape({
    id: PropTypes.string,
    primaryName: PropTypes.string,
    formattedAddress: PropTypes.string,
    status: PropTypes.oneOf(buildingStatuses),
  }),
  space: PropTypes.shape({
    vtsId: PropTypes.number.isRequired,
    vtsPropertyId: PropTypes.number.isRequired,
    suite: PropTypes.string,
    floorName: PropTypes.string.isRequired,
    maximumContiguousArea: areaType,
    remeasuredArea: areaType,
    floorComposition: PropTypes.oneOf(['entire', 'partial']),
    availabilityDate: PropTypes.string,
  }).isRequired,
});

export const locationType = PropTypes.shape({
  pathname: PropTypes.string,
});

export const launchDarklyAllFlagsStateType = PropTypes.shape({
  $flagsState: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      version: PropTypes.number,
      $valid: PropTypes.bool,
    }),
  }),
  $valid: PropTypes.bool,
  [PropTypes.string]: PropTypes.bool,
});

export const buildingSearchResultType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  longitude: PropTypes.number.isRequired,
  latitude: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
});

export const buildingSearchResultsType = PropTypes.arrayOf(buildingSearchResultType);

const coordinateType = PropTypes.shape({ lng: PropTypes.number, lat: PropTypes.number });
const coordinateSetType = PropTypes.arrayOf(coordinateType);

export const marketType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  locality: PropTypes.string.isRequired,
  abbreviatedRegion: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  envelope: coordinateSetType.isRequired,
  boundaries: PropTypes.arrayOf(coordinateSetType).isRequired,
});

export const companyType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  logo: imageType.isRequired,
});

export const globalSearchBuildingType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  primaryName: PropTypes.string.isRequired,
  secondaryName: PropTypes.string,
  cityState: PropTypes.string.isRequired,
});

export const globalSearchLocationType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  locality: PropTypes.string.isRequired,
  abbreviatedRegion: PropTypes.string.isRequired,
});

export const errorType = PropTypes.shape({
  field: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
});

export const dropdownOptionType = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  translationKey: PropTypes.string.isRequired,
});

export const pillOptionType = PropTypes.shape({
  filter: PropTypes.string.isRequired,
  option: PropTypes.string.isRequired,
});

export const childrenType = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.node),
  PropTypes.node,
]);

export const landlordBuilding = PropTypes.shape({
  primaryName: PropTypes.string.isRequired,
  secondaryName: PropTypes.string,
  area: PropTypes.oneOfType([areaType, PropTypes.string]).isRequired,
  location: PropTypes.string.isRequired,
  numberOfAvailabilities: PropTypes.number,
  availableArea: areaType,
  id: PropTypes.string,
  photoUrl: PropTypes.string.isRequired,
  photoDescription: PropTypes.string,
});
